'use client';

import { ComponentProps } from 'react';

import Link from 'next/link';

import { ChevronRight } from 'lucide-react';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Skeleton } from '@/modules/common/components/skeleton';
import { cn } from '@/modules/common/lib/utils';
import { CollectionCard } from '@/modules/product/components/collections/CollectionCard/CollectionCard';
import { ALGOLIA_SEARCH_INDEX } from '@/modules/product/services/consts';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';

import { AlgoliaCollection } from '../../services/collectionTransform';

type CollectionResultCarouselSimplifiedProps = ComponentProps<'section'> & {
  collections: AlgoliaCollection[];
  query: string;
  collectionNbHits: number;
  isLoading?: boolean;
};

export const CollectionResultCarouselSimplified = ({
  className,
  collections,
  query,
  collectionNbHits,
  isLoading = false,
  ...props
}: CollectionResultCarouselSimplifiedProps) => {
  const total = collectionNbHits || 0;
  const showMore = total > 4;

  const inspirationQuery = query.trim();
  const seeMoreHref = `/search/inspiration?query=${encodeURIComponent(inspirationQuery)}`;

  if (isLoading) {
    return <Skeleton className={cn('h-28 w-full border-gray-200 p-4 lg:p-8', className)} />;
  }

  if (!collections?.length || collections.length < 4) {
    return null;
  }

  const lastCollection = collections[collections.length - 1];

  return (
    <section
      className={cn('bg-gray-75 px-4 py-6 md:rounded-2xl md:px-6 md:py-8', className)}
      {...props}
    >
      <header className="mb-5 flex items-center justify-between gap-6">
        <div>
          <h2 className="text-base font-bold md:text-[28px]/[32px]">
            {inspirationQuery ? `Results for “${inspirationQuery}” in Inspiration` : 'Inspirations'}
          </h2>
        </div>

        {showMore && (
          <div className="hidden lg:block">
            <Link className="font-base flex items-center gap-1 hover:underline" href={seeMoreHref}>
              See more
              <ChevronRight className="h-6 w-6" />
            </Link>
          </div>
        )}
      </header>

      <ul className="flex gap-1 overflow-x-auto pb-2 lg:grid lg:grid-cols-4 lg:gap-4 lg:overflow-visible lg:pb-0">
        {collections.map((collection, index) => (
          <li
            key={collection.slug ?? index}
            className="w-[45%] flex-shrink-0 overflow-hidden rounded-lg md:w-[36%] lg:w-full"
          >
            <CollectionCard
              {...collection}
              feature={DataLayerFeature.Search}
              itemListId={ALGOLIA_SEARCH_INDEX}
              itemListName="Search results"
              itemListType="PLP"
              classNameImg="rounded-none aspect-[157/148] lg:aspect-[45/34]"
              className="flex h-full flex-col bg-white"
              variant="collection-card"
              wrapperClassName="h-full flex flex-col justify-between"
              classNameCollectionName="text-[13px] lg:text-base"
              classNameButton="text-sm lg:text-base px-3 py-1 h-fit w-full"
              where={DataLayerWhere.Brand}
            />
          </li>
        ))}

        {lastCollection && showMore && (
          <li className="relative w-[45%] flex-shrink-0 overflow-hidden rounded-lg md:w-[36%] lg:hidden lg:w-full">
            <Link
              href={seeMoreHref}
              className="absolute inset-0 z-10 flex place-content-center items-center justify-center bg-black bg-opacity-20 text-white"
            >
              See more <ChevronRight className="h-6 w-6" />
            </Link>
            <CollectionCard
              feature={DataLayerFeature.Search}
              {...lastCollection}
              itemListId={ALGOLIA_SEARCH_INDEX}
              itemListName="Search results"
              itemListType="PLP"
              classNameImg="rounded-none aspect-[157/148] lg:aspect-[45/34]"
              variant="collection-card"
              className="pointer-events-none flex h-full flex-col bg-white blur"
              wrapperClassName="h-full flex flex-col justify-between"
              classNameCollectionName="text-[13px] lg:text-base"
              classNameButton="text-sm lg:text-base px-3 py-1 h-fit w-full"
              where={DataLayerWhere.Brand}
            />
          </li>
        )}
      </ul>
    </section>
  );
};
